import React from "react";
import dayjs from "dayjs";
import { View, Text, StyleSheet } from "../platformComponents";
import {
  decimalPointNoMoreX,
  i18nCrossUtil,
  IS_WEB,
  numberToThousands,
  tranformPx,
} from "@adgm/libs";
import { FiatOrderV2VO } from "@adgm/cross-models";

interface FiatWithdrawalDetailProps {
  detailData: FiatOrderV2VO | null;
}

export const fiatWithdrawalStatusMap = {
  APPLY: "Pending",
  APPROVED1: "Pending",
  APPROVED2: "Pending",
  REJECTED: "Rejected",
  COMPLETED: "Success",
};

export const FiatWithdrawalDetail = ({ detailData }: FiatWithdrawalDetailProps) => {
  const {
    amount = "",
    currency = "",
    created = "",
    bankName = "--",
    swiftBicCode = "--",
    bankCode = "--",
    bankBranchCode = "--",
    bankAddress = "--",
    bankAccountName = "--",
    bankAccountNumber = "--",
    status = "",
    remark = "--",
  } = detailData || {};

  const intl = i18nCrossUtil.t();
  const displayAmount = `${numberToThousands(decimalPointNoMoreX(amount, 2))} ${currency}`;
  return (
    <View style={styles.content}>
      {IS_WEB ? (
        <View style={styles.rowGroup}>
          <View style={styles.infoItem}>
            <Text style={styles.label}>{intl["common.amount"]}</Text>
            <Text style={styles.value}>{displayAmount}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.label}>{intl["common.time"]}</Text>
            <Text style={styles.value}>{dayjs(created).format("YYYY-MM-DD HH:mm:ss")}</Text>
          </View>
        </View>
      ) : (
        <View
          style={{
            width: "100%",
            flexDirection: "row",
            justifyContent: "space-between",
          }}>
          <Text style={styles.value}>{dayjs(created).format("YYYY-MM-DD HH:mm:ss")}</Text>
          <Text style={styles.value}>{displayAmount}</Text>
        </View>
      )}

      <View style={styles.section}>
        <Text style={styles.sectionHeaderText}>{intl["wallet.withdraw.fiat.bank.info"]}</Text>
        <View style={styles.rowGroup}>
          <View style={styles.infoItem}>
            <Text style={styles.label}>{intl["wallet.withdraw.fiat.bank.name.label"]}</Text>
            <Text style={styles.value}>{bankName}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.label}>{intl["wallet.withdraw.fiat.swift.bic.code.label"]}</Text>
            <Text style={styles.value}>{swiftBicCode}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.label}>{intl["wallet.withdraw.fiat.bank.code.label"]}</Text>
            <Text style={styles.value}>{bankCode}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.label}>{intl["wallet.withdraw.fiat.branch.code.label"]}</Text>
            <Text style={styles.value}>{bankBranchCode}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.label}>{intl["wallet.withdraw.fiat.bank.address.label"]}</Text>
            <Text style={styles.value}>{bankAddress}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <Text style={styles.sectionHeaderText}>{intl["wallet.withdraw.fiat.account.info"]}</Text>
        <View style={styles.rowGroup}>
          <View style={styles.infoItem}>
            <Text style={styles.label}>
              {intl["wallet.withdraw.fiat.account.holder.name.label"]}
            </Text>
            <Text style={styles.value}>{bankAccountName}</Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.label}>
              {intl["wallet.withdraw.fiat.bank.account.number.label"]}
            </Text>
            <Text style={styles.value}>{bankAccountNumber}</Text>
          </View>
        </View>
      </View>

      <View style={styles.section}>
        <View style={styles.rowGroup}>
          <View style={styles.infoItem}>
            <Text style={styles.label}>{intl["common.status"]}</Text>
            <Text style={styles.value}>
              {fiatWithdrawalStatusMap[status]?.toLowerCase() || "--"}
            </Text>
          </View>
          <View style={styles.infoItem}>
            <Text style={styles.label}>{intl["common.reason"]}</Text>
            <Text style={styles.value}>{remark}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    display: "flex",
    flexDirection: "column",
    ...(!IS_WEB && {
      paddingTop: tranformPx(24),
    }),
  },
  section: {
    marginTop: 32,
    width: "100%",
  },

  sectionHeaderText: {
    fontSize: tranformPx(14),
    fontWeight: "400",
    color: "#9E9E9E",
    ...(IS_WEB && {
      fontWeight: "700",
      color: "rgba(255, 255, 255, 0.85)",
    }),
  },
  rowGroup: {
    display: "flex",
    width: "100%",
    flexDirection: "column",
    ...(IS_WEB && {
      flexDirection: "row",
      flexWrap: "wrap",
      justifyContent: "space-between", // Add space between columns
    }),
  },
  infoItem: {
    display: "flex",
    width: "100%",
    justifyContent: "space-between",
    flexDirection: "row",
    marginTop: 16,
    ...(IS_WEB && {
      width: "48%", // Slightly less than 50% to ensure space between
    }),
  },
  label: {
    fontSize: tranformPx(14),
    fontWeight: "400",
    color: "#ffffff",
    ...(IS_WEB && {
      color: "rgba(255, 255, 255, 0.60)",
    }),
    marginRight: tranformPx(12),
  },
  value: {
    flexShrink: 1,
    fontSize: tranformPx(14),
    fontWeight: "500",
    color: "#ffffff",
    ...(IS_WEB && {
      overflow: "hidden",
      whiteSpace: "wrap",
      textAlign: "right",
    }),
  },
});

export default FiatWithdrawalDetail;
